import React from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_VALUES, getDateFormat, formatTimeUnit } from '../../../../../../../Apis/functions';
import { IconClockTable, IconPeronClock } from '../../../../../../../components/buttons/IconButtons';
import { formatCurrency } from './PayrollLinesColumn';
import CustomTooltip from "../../../../../../../components/CustomTooltip";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Summary cards (expected/worked/extra hours + net pay) for a payroll period.
const PayrollSummaryCards = ({ data }) => {
    const { t } = useTranslation();

    const summaryCards = [
        { key:'EXPECTED_HOURS', label: t('expected_hours'), value: `${formatTimeUnit(data?.totalExpectedMinutes ?? 0)}`, legend: `${data?.totalAttendances ?? 0} ${t('attendances').toLowerCase()}`, legendIcon: <IconPeronClock width={15} height={15}/>, colorBg: 'background-shades-50', colorBorder: 'border-full-shades-150', colorFont: 'font-color-shades-700', cols: 'col-sm-12 col-md-2 px-1 mb-2' },
        { key:'WORKED_HOURS', label: t('worked_hours'), value: `${formatTimeUnit(data?.totalWorkedMinutes ?? 0)}`, legend: `${data?.totalWorkedAttendances ?? 0} ${t('attendances').toLowerCase()}`, legendIcon: <IconPeronClock width={15} height={15}/>, colorBg: 'background-shades-50', colorBorder: 'border-full-shades-150', colorFont: 'font-color-shades-700', cols: 'col-sm-12 col-md-2 px-1 mb-2' },
        { key:'EXTRA_HOURS', label: t('extra_hours'), value: `${formatTimeUnit(data?.totalExceededMinutes ?? 0)}`, colorBg: 'background-shades-50', colorBorder: 'border-full-shades-150', colorFont: 'font-color-shades-700', cols: 'col-sm-12 col-md-2 px-1 mb-2' },
        { key:'BASE_SALARY', label: t('base_salary'), value: `${formatCurrency(data?.baseSalary ?? 0)}`, colorBg: 'background-warning-50', colorBorder: 'border-full-warning-150', colorFont: 'font-color-warning-700', cols: 'col-sm-12 col-md-3 px-1 mb-2' },

        { key:'NET_PAY', label: t('net_pay'), value: <>
                <CustomTooltip
                    text={`${formatCurrency(data?.netPay)}`}
                    classes={'fs-24px ibl-font-w-600 text-ellipsis-1-row'}
                    showIfTruncated={true}
                    placement="top"
                />
                <div className={'d-flex justify-content-start align-items-center'}>
                    <div className={'mr-2 icon-color-success-300 d-flex align-items-center'}><IconClockTable/></div>
                    <p className='fs-10px ibl-font-w-500'>
                        {t('calculated_at')}: {getDateFormat(data?.calculatedAt, true) ?? EMPTY_VALUES.DEFAULT}
                    </p>
                </div>
        </>, colorBg: 'background-success-50', colorBorder: 'border-full-success-150', colorFont: 'font-color-success-700', cols: 'col-sm-12 col-md-3 px-1 mb-2' },
    ];

    return (
        <div className='row mb-3'>
            {summaryCards.map((card, i) => (
                <div key={i} className={`${card.cols} d-flex`}>
                    <div className={`h-100 w-100 p-3 ${card.colorBg} ${card.colorBorder} card-border-8px`}>
                        <p className={`fs-12px ibl-font-w-500 ${card.colorFont}`}>{card.label}</p>
                        { card.key === 'NET_PAY'
                            ?    <div className={`fs-20px ibl-font-w-700 ${card.colorFont}`}>{card.value}</div>
                            :
                            <CustomTooltip
                                text={`${card.value}`}
                                classes={`fs-20px ibl-font-w-700 ${card.colorFont} text-ellipsis-1-row flex-grow-1`}
                                showIfTruncated={true}
                                placement="top"
                            />
                        }

                        { card.legend &&
                            <div className={'d-flex justify-content-start align-items-center'}>
                                <div className={'mr-2 icon-color-shades-300 d-flex align-items-center'}>{card.legendIcon}</div>
                                <p className={`fs-10px ibl-font-w-500 ${card.colorFont}`}>{card.legend}</p>
                            </div>
                        }
                    </div>
                </div>
            ))}
        </div>
    );
};

export default PayrollSummaryCards;
